/* eslint-disable unicorn/no-array-reduce */
/* eslint-disable no-param-reassign */
import { isNonNullish } from 'remeda'
import type { Product, SeasonID, UseCategoryCollection } from '@velasca/types'
import { getProductCardImage } from '@velasca/utils/get-product-card-image'
import { getVariantDot } from '@velasca/utils/get-variant-dot'
import { $settings } from '@velasca/utils/globals'
import { isMediaImage } from '@velasca/utils/is-media-image'
import { isSeason } from '@velasca/utils/is-season'

export const formatApparelData = (
  collection: UseCategoryCollection,
  areCollectionCardImagesWornApparel = false,
): Product[] | null => {
  if (!collection) {
    return null
  }

  const data = collection.products.edges.map(
    ({
      node: {
        title,
        handle,
        id,
        material,
        materialShort,
        color,
        colorShort,
        textureImageName,
        variantImage,
        variantColors,
        variantColorStyle,
        soleColorHex,
        media,
        priceRange,
        compareAtPriceRange,
        reviewsCount,
        reviewsAverage,
        productType,
        staffPickIconID,
        isNew,
        frontImageIndex,
        hoverImageIndex,
        collectionBrandID,
        collectionBrandName,
        seasonID,
        seasonName,
        customProductType,
        jolly,
      },
    }) => {
      const validSeasonID =
        seasonID?.value && isSeason(parseInt(seasonID.value))
          ? (parseInt(seasonID.value) as SeasonID)
          : undefined

      const flatImages = media.edges
        .map(({ node }) => node)
        .filter(isMediaImage)
        .map(({ image }) => image?.url)
        .filter(isNonNullish)
      const img = getProductCardImage({
        images: flatImages,
        customProductType: customProductType?.value
          ? parseInt(customProductType.value)
          : undefined,
        frontImageIndex: frontImageIndex?.value
          ? parseInt(frontImageIndex.value)
          : undefined,
        hoverImageIndex: hoverImageIndex?.value
          ? parseInt(hoverImageIndex.value)
          : undefined,
        areCollectionCardImagesWornApparel,
      })

      const variant = {
        handle,
        id,
        price: {
          max: parseFloat(priceRange?.maxVariantPrice?.amount || '0'),
          min: parseFloat(priceRange?.minVariantPrice?.amount || '0'),
        },
        compareAtPrice: {
          max: parseFloat(compareAtPriceRange.maxVariantPrice.amount || '0'),
          min: parseFloat(compareAtPriceRange.minVariantPrice.amount || '0'),
        },
        description: {
          bold: colorShort?.value ?? color?.value ?? null,
          thin: materialShort?.value ?? material?.value ?? null,
        },
        img,
        reviews: {
          count: reviewsCount?.value || null,
          average: reviewsAverage?.value || null,
        },
        variantDot: getVariantDot({
          variantColors,
          soleColorHex,
          variantColorStyle,
          textureImageName,
          variantImage,
          imageAssetPublicPath: $settings.imageAssetPublicPath,
        }),
        staffPickIconID: staffPickIconID?.value || null,
        isNew: isNew?.value === 'true',
        collectionBrandName: collectionBrandName?.value,
        seasonID: validSeasonID,
        seasonName: seasonName?.value,
        jolly: jolly?.value,
      }

      return {
        title,
        productType,
        collectionBrandID: collectionBrandID?.value,
        variants: [variant],
      }
    },
  )

  return data
}

export const groupByProduct = (
  collection: UseCategoryCollection,
  areCollectionCardImagesWornApparel = false,
) => {
  if (!collection) {
    return null
  }

  const data = collection.products.edges.reduce<Product[]>(
    (
      acc,
      {
        node: {
          title,
          handle,
          id,
          material,
          materialShort,
          color,
          colorShort,
          media,
          priceRange,
          compareAtPriceRange,
          variantColors,
          variantColorStyle,
          soleColorHex,
          textureImageName,
          variantImage,
          reviewsCount,
          reviewsAverage,
          productType,
          staffPickIconID,
          isNew,
          collectionBrandID,
          collectionBrandName,
          seasonID,
          seasonName,
          customProductType,
          jolly,
          frontImageIndex,
          hoverImageIndex,
        },
      },
    ) => {
      const index = acc.findIndex((e) => e.title === title)

      const validSeasonID =
        seasonID?.value && isSeason(parseInt(seasonID.value))
          ? (parseInt(seasonID.value) as SeasonID)
          : undefined

      const flatImages = media.edges
        .map(({ node }) => node)
        .filter(isMediaImage)
        .map(({ image }) => image?.url)
        .filter(isNonNullish)
      const img = getProductCardImage({
        images: flatImages,
        customProductType: customProductType?.value
          ? parseInt(customProductType.value)
          : undefined,
        frontImageIndex: frontImageIndex?.value
          ? parseInt(frontImageIndex.value)
          : undefined,
        hoverImageIndex: hoverImageIndex?.value
          ? parseInt(hoverImageIndex.value)
          : undefined,
        areCollectionCardImagesWornApparel,
      })

      const variant = {
        handle,
        id,
        price: {
          max: parseFloat(priceRange?.maxVariantPrice?.amount || '0'),
          min: parseFloat(priceRange?.minVariantPrice?.amount || '0'),
        },
        compareAtPrice: {
          max: parseFloat(compareAtPriceRange.maxVariantPrice.amount || '0'),
          min: parseFloat(compareAtPriceRange.minVariantPrice.amount || '0'),
        },
        description: {
          bold: colorShort?.value ?? color?.value ?? null,
          thin: materialShort?.value ?? material?.value ?? null,
        },
        img,
        variantDot: getVariantDot({
          variantColors,
          soleColorHex,
          variantColorStyle,
          textureImageName,
          variantImage,
          imageAssetPublicPath: $settings.imageAssetPublicPath,
        }),
        reviews: {
          count: reviewsCount?.value || null,
          average: reviewsAverage?.value || null,
        },
        staffPickIconID: staffPickIconID?.value || null,
        isNew: isNew?.value === 'true',
        collectionBrandName: collectionBrandName?.value,
        seasonID: validSeasonID,
        seasonName: seasonName?.value,
        jolly: jolly?.value,
      }

      if (index > -1) {
        const variants = acc[index].variants || []

        variants.push(variant)

        acc[index].variants = variants
      } else {
        acc.push({
          title,
          productType,
          collectionBrandID: collectionBrandID?.value,
          variants: [variant],
        })
      }

      return acc
    },
    [],
  )

  return data
}
